import React, { useEffect, useMemo, useRef, useState } from 'react';
import {
  createAssetProcessingDoc,
  exportDomToPdf,
  failAssetDoc,
  finalizeAssetDoc,
  uploadBlobToStorage,
} from './printablesExport';
import {
  formatBytes,
  getImageDimensions,
  loadQrPrefs,
  saveQrPrefs,
  uploadPosterImage,
  uploadQrToStorage,
  validateQrFile,
  validatePosterImage,
} from './qrUploadUtils';
import {
  QR_POSTER_FIELDS,
  QR_POSTER_TEMPLATES,
  QRPosterTemplateClassic,
  QRPosterTemplateNoir,
} from './QRPosterTemplates';
import './QRPosterPrintable.css';

const PAGE_SIZE_MM = { width: 210, height: 297 };
const MAX_QR_SIZE_LABEL = '2MB max / PNG/JPG only / 600x600px minimum';
const HERO_SIZE_LABEL = '5MB max / PNG or JPG only / 1200px width recommended';
const BUSINESS_CARD_LOGO_URL = '/d_logo-removebg-preview.png';

const DEFAULT_TEMPLATES = {
  classic: {
    headline: 'Scan to Connect',
    subheadline: 'WhatsApp us or view listings',
    note: 'Open your camera and scan',
    contactLine: '{PHONE} | {EMAIL}',
    addressLine: '{ADDRESS}',
    reraLine: 'RERA: {RERA}',
  },
  'noir-luxe': {
    brandTag: 'Exclusive Preview',
    headlineLine1: 'Discover',
    headlineEmphasis: 'Luxury',
    headlineLine2: 'Redefined',
    subheadline: 'Where elegance meets innovation',
    variant: 'dark',
    qrLabel: 'Scan to Connect',
    qrActionLine1: 'Unlock Your',
    qrActionLine2: 'Exclusive Access',
    qrUrl: '',
    footerBrandName: '{BRAND}',
    footerTagline: '',
    footerLine1: '{PHONE}',
    footerLine2: '{EMAIL}',
  },
};

const mergeTemplateDefaults = (defaults, saved) => {
  const merged = {};
  Object.keys(defaults).forEach((key) => {
    merged[key] = { ...defaults[key], ...(saved?.[key] || {}) };
  });
  return merged;
};

const buildMissing = ({ compliance, profile, branch }) => {
  const missing = [];
  if (compliance.requirePhone && !profile.phone) missing.push('phone');
  if (compliance.requireAddress && !branch.address) missing.push('address');
  if (compliance.requireRera && !branch.reraId) missing.push('rera');
  return missing;
};

const QRPosterPrintable = ({
  firebase,
  authUser,
  tenantId,
  branchId,
  profile,
  branch,
  brandKit,
  assignedEmail,
  assignedEmailMissing,
  compliance,
}) => {
  const pageRef = useRef(null);
  const fileInputRef = useRef(null);
  const heroInputRef = useRef(null);
  const saveTimerRef = useRef(null);

  const [qrImageUrl, setQrImageUrl] = useState('');
  const [qrMeta, setQrMeta] = useState({
    fileName: '',
    fileSize: 0,
    sizeLabel: '',
    width: 0,
    height: 0,
    storagePath: '',
    updatedAt: null,
  });
  const [uploading, setUploading] = useState(false);
  const [uploadError, setUploadError] = useState('');
  const [uploadMessage, setUploadMessage] = useState('');

  const [templateKey, setTemplateKey] = useState(QR_POSTER_TEMPLATES[0]?.key || 'classic');
  const [templateFields, setTemplateFields] = useState(DEFAULT_TEMPLATES);
  const [heroImage, setHeroImage] = useState({
    url: '',
    storagePath: '',
    fileName: '',
    fileSize: 0,
    width: 0,
    height: 0,
    sizeLabel: '',
  });
  const [heroUploading, setHeroUploading] = useState(false);
  const [heroError, setHeroError] = useState('');
  const [heroWarning, setHeroWarning] = useState('');
  const [prefsLoaded, setPrefsLoaded] = useState(false);

  const [saveToAssets, setSaveToAssets] = useState(true);
  const [exporting, setExporting] = useState(false);
  const [message, setMessage] = useState('');
  const [error, setError] = useState('');
  const [warning, setWarning] = useState('');

  const missing = useMemo(
    () => buildMissing({ compliance, profile, branch }),
    [branch, compliance, profile],
  );
  const qrMissing = !qrImageUrl;
  const canSave = Boolean(tenantId && branchId && authUser?.uid);

  useEffect(() => {
    let active = true;

    const loadPrefs = async () => {
      if (!tenantId || !authUser?.uid || !firebase) return;
      try {
        const prefs = await loadQrPrefs(firebase, tenantId, authUser.uid);
        if (!active) return;
        const mergedTemplates = mergeTemplateDefaults(DEFAULT_TEMPLATES, prefs?.templates);
        const noirTemplate = mergedTemplates['noir-luxe'] || {};
        const cleanedTemplates = {
          ...mergedTemplates,
          'noir-luxe': {
            ...noirTemplate,
            qrUrl: noirTemplate.qrUrl === '{WEBSITE}' ? '' : noirTemplate.qrUrl,
            footerTagline:
              noirTemplate.footerTagline === 'Authorized Franchisee' ? '' : noirTemplate.footerTagline,
          },
        };
        const nextTemplateKey =
          QR_POSTER_TEMPLATES.find((template) => template.key === prefs?.templateKey)?.key ||
          QR_POSTER_TEMPLATES[0]?.key ||
          'classic';

        setTemplateFields(cleanedTemplates);
        setTemplateKey(nextTemplateKey);

        const hero = prefs?.images?.hero || {};
        setHeroImage({
          url: hero.url || '',
          storagePath: hero.storagePath || '',
          fileName: hero.fileName || '',
          fileSize: hero.fileSize || 0,
          width: hero.width || 0,
          height: hero.height || 0,
          sizeLabel: hero.fileSize ? formatBytes(hero.fileSize) : '',
        });

        setQrImageUrl(prefs?.qrImageUrl || '');
        setQrMeta({
          fileName: prefs?.fileName || 'QR image',
          fileSize: prefs?.fileSize || 0,
          sizeLabel: prefs?.fileSize ? formatBytes(prefs.fileSize) : 'N/A',
          width: prefs?.width || 0,
          height: prefs?.height || 0,
          storagePath: prefs?.storagePath || '',
          updatedAt: prefs?.updatedAt || null,
        });
      } catch (loadError) {
        console.error('QR prefs load failed:', loadError);
      } finally {
        if (active) setPrefsLoaded(true);
      }
    };

    loadPrefs();
    return () => {
      active = false;
    };
  }, [authUser?.uid, firebase, tenantId]);

  useEffect(() => {
    if (!prefsLoaded || !tenantId || !authUser?.uid || !firebase) return undefined;
    if (saveTimerRef.current) {
      clearTimeout(saveTimerRef.current);
    }
    saveTimerRef.current = setTimeout(() => {
      const payload = {
        qrImageUrl,
        storagePath: qrMeta.storagePath,
        fileName: qrMeta.fileName,
        fileSize: qrMeta.fileSize,
        width: qrMeta.width,
        height: qrMeta.height,
        templateKey,
        templates: templateFields,
        images: {
          hero: {
            url: heroImage.url,
            storagePath: heroImage.storagePath,
            fileName: heroImage.fileName,
            fileSize: heroImage.fileSize,
            width: heroImage.width,
            height: heroImage.height,
          },
        },
      };

      saveQrPrefs(firebase, tenantId, authUser.uid, payload).catch((saveError) => {
        console.error('QR prefs save failed:', saveError);
      });
    }, 600);

    return () => {
      if (saveTimerRef.current) {
        clearTimeout(saveTimerRef.current);
      }
    };
  }, [authUser?.uid, firebase, heroImage, prefsLoaded, qrImageUrl, qrMeta, tenantId, templateFields, templateKey]);

  const handleFilePick = () => {
    if (fileInputRef.current) {
      fileInputRef.current.click();
    }
  };

  const handleFileChange = async (event) => {
    const file = event.target.files?.[0];
    if (!file) return;

    setUploadError('');
    setUploadMessage('');

    const validation = validateQrFile(file);
    if (!validation.isValid) {
      setUploadError(validation.errors.join(' '));
      return;
    }

    try {
      const dimensions = await getImageDimensions(file);
      if (dimensions.width < 600 || dimensions.height < 600) {
        setUploadError('Minimum resolution is 600x600px for print quality.');
        return;
      }

      if (!firebase || !tenantId || !authUser?.uid) {
        setUploadError('Upload unavailable. Please try again later.');
        return;
      }

      setUploading(true);
      const uploadResult = await uploadQrToStorage({
        firebase,
        tenantId,
        uid: authUser.uid,
        file,
      });

      const meta = {
        fileName: file.name || 'qr-image',
        fileSize: file.size,
        width: dimensions.width,
        height: dimensions.height,
        storagePath: uploadResult.storagePath,
      };

      setQrImageUrl(uploadResult.qrImageUrl);
      setQrMeta({
        fileName: meta.fileName,
        fileSize: meta.fileSize,
        sizeLabel: formatBytes(meta.fileSize),
        width: meta.width,
        height: meta.height,
        storagePath: meta.storagePath,
        updatedAt: new Date().toISOString(),
      });
      setUploadMessage('QR image uploaded.');
    } catch (err) {
      console.error('QR upload failed:', err);
      setUploadError('Upload failed. Please try again.');
    } finally {
      setUploading(false);
      if (event.target) {
        event.target.value = '';
      }
    }
  };

  const handleHeroPick = () => {
    if (heroInputRef.current) {
      heroInputRef.current.click();
    }
  };

  const handleHeroChange = async (event) => {
    const file = event.target.files?.[0];
    if (!file) return;

    setHeroError('');
    setHeroWarning('');

    const validation = validatePosterImage(file, 5);
    if (!validation.isValid) {
      setHeroError(validation.errors.join(' '));
      return;
    }

    try {
      const dimensions = await getImageDimensions(file);
      if (dimensions.width < 1200) {
        setHeroWarning('Recommended minimum width is 1200px.');
      }

      if (!firebase || !tenantId || !authUser?.uid) {
        setHeroError('Upload unavailable. Please try again later.');
        return;
      }

      setHeroUploading(true);
      const uploadResult = await uploadPosterImage({
        firebase,
        tenantId,
        uid: authUser.uid,
        file,
        slot: 'poster-hero',
      });

      setHeroImage({
        url: uploadResult.imageUrl,
        storagePath: uploadResult.storagePath,
        fileName: file.name || 'poster-hero',
        fileSize: file.size,
        width: dimensions.width,
        height: dimensions.height,
        sizeLabel: formatBytes(file.size),
      });
    } catch (err) {
      console.error('Poster image upload failed:', err);
      setHeroError('Upload failed. Please try again.');
    } finally {
      setHeroUploading(false);
      if (event.target) {
        event.target.value = '';
      }
    }
  };

  const handleTemplateChange = (event) => {
    const nextKey = event.target.value;
    setTemplateKey(nextKey);
    setTemplateFields((prev) => {
      if (prev?.[nextKey]) return prev;
      return { ...prev, [nextKey]: DEFAULT_TEMPLATES[nextKey] || {} };
    });
  };

  const updateTemplateField = (fieldKey, value) => {
    setTemplateFields((prev) => ({
      ...prev,
      [templateKey]: {
        ...(prev?.[templateKey] || DEFAULT_TEMPLATES[templateKey] || {}),
        [fieldKey]: value,
      },
    }));
  };

  const handleExport = async () => {
    if (isBlocked || !pageRef.current) return;
    setExporting(true);
    setError('');
    setWarning('');
    setMessage('');

    let assetRef = null;
    try {
      const fileName = `Douglas-McCartney-QR-Poster-${profile.fullName || 'franchisee'}.pdf`;
      const blob = await exportDomToPdf({
        element: pageRef.current,
        fileName,
        pageSizeMm: PAGE_SIZE_MM,
        backgroundColor: exportBackground,
        marginMm: 8,
      });

      if (saveToAssets) {
        if (!canSave) {
          setWarning('Upload failed, file not saved.');
        } else {
          try {
            const snapshotLogoUrl =
              templateKey === 'classic' ? BUSINESS_CARD_LOGO_URL : brandKit?.logoUrl || '';
            const snapshot = {
              fullName: profile.fullName || '',
              designation: profile.designation || '',
              phone: profile.phone || '',
              assignedEmail: assignedEmail || '',
              address: branch.address || '',
              city: branch.city || '',
              reraId: branch.reraId || '',
              qrImageUrl,
              templateKey,
              templateFields: activeFields,
              heroImageUrl: heroImage.url || '',
              brand: {
                colors: brandKit?.colors || {},
                disclaimerText: brandKit?.disclaimerText || '',
                logoUrl: snapshotLogoUrl,
              },
            };

            assetRef = await createAssetProcessingDoc({
              firebase,
              tenantId,
              branchId,
              authUserId: authUser?.uid || '',
              type: 'qr_poster',
              snapshot,
            });

            const storagePath = `tenants/${tenantId}/assets/${assetRef.id}/qr-poster.pdf`;
            const url = await uploadBlobToStorage({
              firebase,
              storagePath,
              blob,
              contentType: 'application/pdf',
              metadata: { tenantId, branchId, createdBy: authUser?.uid || '' },
            });

            await finalizeAssetDoc({
              firebase,
              assetRef,
              output: { pdfUrl: url, storagePath },
            });
          } catch (uploadError) {
            console.error('QR poster asset upload failed:', uploadError);
            setWarning('Upload failed, file not saved.');
            if (assetRef) {
              await failAssetDoc({ firebase, assetRef, errorMessage: uploadError.message });
            }
          }
        }
      }
      setMessage('QR poster downloaded.');
    } catch (err) {
      console.error('QR poster export failed:', err);
      setError('Could not generate PDF. Try again.');
    } finally {
      setExporting(false);
    }
  };

  const accentColor = brandKit?.colors?.accent || '#c8a951';
  const headlineFont = brandKit?.fonts?.primary ? brandKit.fonts.primary : 'var(--font-serif, serif)';
  const bodyFont = brandKit?.fonts?.secondary ? brandKit.fonts.secondary : 'var(--font-sans, sans-serif)';
  const disclaimerText = brandKit?.disclaimerText || '';
  const brandName = branch.branchName || 'Douglas & McCartney';
  const website = branch.website || 'douglasmccartney.com';

  const placeholders = useMemo(
    () => ({
      BRAND: brandName,
      PHONE: profile.phone || '',
      EMAIL: assignedEmail || '',
      ADDRESS: branch.address || '',
      RERA: branch.reraId || '',
      CITY: branch.city || '',
      WEBSITE: website,
      WHATSAPP: profile.whatsapp || '',
    }),
    [assignedEmail, brandName, branch.address, branch.city, branch.reraId, profile.phone, profile.whatsapp, website],
  );

  const templateConfig =
    QR_POSTER_TEMPLATES.find((template) => template.key === templateKey) || QR_POSTER_TEMPLATES[0];
  const activeFields = templateFields?.[templateKey] || DEFAULT_TEMPLATES[templateKey] || {};
  const fieldDefs = QR_POSTER_FIELDS?.[templateKey] || [];
  const noirVariant = activeFields.variant === 'light' ? 'light' : 'dark';
  const isNoirLight = templateKey === 'noir-luxe' && noirVariant === 'light';
  const heroRequired = Boolean(templateConfig?.requiresHero);
  const heroMissing = heroRequired && !heroImage.url;
  const exportBackground = isNoirLight ? '#f5f0e8' : templateConfig?.backgroundColor || '#ffffff';
  const isBlocked = missing.length > 0 || qrMissing || heroMissing;

  return (
    <div className="printable-layout">
      <div className="printable-preview">
        <div
          className={`printable-page qrposter-page ${
            templateKey === 'noir-luxe' && !isNoirLight ? 'qrposter-page--dark' : ''
          }`}
          ref={pageRef}
        >
          {templateKey === 'noir-luxe' ? (
            <QRPosterTemplateNoir
              fields={activeFields}
              placeholders={placeholders}
              logoUrl={brandKit?.logoUrl || ''}
              accentColor={accentColor}
              qrImageUrl={qrImageUrl}
              heroImageUrl={heroImage.url}
              variant={noirVariant}
            />
          ) : (
            <QRPosterTemplateClassic
              fields={activeFields}
              placeholders={placeholders}
              logoUrl={BUSINESS_CARD_LOGO_URL}
              accentColor={accentColor}
              headlineFont={headlineFont}
              bodyFont={bodyFont}
              qrImageUrl={qrImageUrl}
              showAddress={compliance.requireAddress && branch.address}
              showRera={compliance.requireRera && branch.reraId}
              disclaimerText={disclaimerText}
            />
          )}
        </div>
      </div>

      <div className="printable-panel">
        <div className={`printable-status ${isBlocked ? 'error' : 'success'}`}>
          {isBlocked ? 'Missing Requirements' : 'Ready'}
        </div>
        {assignedEmailMissing && (
          <div className="printable-banner warning">Assigned email not set yet.</div>
        )}
        {qrMissing && (
          <div className="printable-banner error">
            Upload QR image to download poster.
          </div>
        )}
        {missing.length > 0 && (
          <div className="printable-banner error">
            Missing: {missing.join(', ')}.
            <button type="button" className="printable-link" onClick={() => window.location.assign('/account')}>
              Fix in Profile
            </button>
          </div>
        )}
        {heroMissing && (
          <div className="printable-banner warning">Upload hero image for this template.</div>
        )}
        {uploadError && <div className="printable-banner error">{uploadError}</div>}
        {uploadMessage && <div className="printable-banner info">{uploadMessage}</div>}
        {heroError && <div className="printable-banner error">{heroError}</div>}
        {message && <div className="printable-banner info">{message}</div>}
        {warning && <div className="printable-banner warning">{warning}</div>}
        {error && <div className="printable-banner error">{error}</div>}

        <div className="qrposter-section">
          <h4>Template</h4>
          <label className="qrposter-field">
            Template
            <select value={templateKey} onChange={handleTemplateChange} className="qrposter-select">
              {QR_POSTER_TEMPLATES.map((template) => (
                <option key={template.key} value={template.key}>
                  {template.label}
                </option>
              ))}
            </select>
          </label>
          {templateKey === 'noir-luxe' && (
            <label className="qrposter-field">
              Variant
              <select
                value={noirVariant}
                onChange={(event) => updateTemplateField('variant', event.target.value)}
                className="qrposter-select"
              >
                <option value="dark">Dark</option>
                <option value="light">Light</option>
              </select>
            </label>
          )}

          {heroRequired && (
            <div className="qrposter-hero-upload">
              <div className="qrposter-hero-row">
                <div>
                  <strong>Hero image</strong>
                  <div className="qrposter-upload-hint">{HERO_SIZE_LABEL}</div>
                  {heroWarning && <div className="qrposter-upload-warning">{heroWarning}</div>}
                </div>
                <button
                  type="button"
                  className="printable-action"
                  onClick={handleHeroPick}
                  disabled={heroUploading}
                >
                  {heroImage.url ? 'Replace Hero' : 'Upload Hero'}
                </button>
              </div>
              <input
                ref={heroInputRef}
                type="file"
                accept="image/png,image/jpeg"
                onChange={handleHeroChange}
                style={{ display: 'none' }}
              />
              <div className="qrposter-upload-meta">
                <div>Status: <strong>{heroImage.url ? 'Uploaded' : 'Not uploaded'}</strong></div>
                <div>File: {heroImage.fileName || 'N/A'}</div>
                <div>
                  Resolution:{' '}
                  {heroImage.width && heroImage.height
                    ? `${heroImage.width}x${heroImage.height}px`
                    : 'N/A'}
                </div>
                <div>Size: {heroImage.sizeLabel || 'N/A'}</div>
              </div>
            </div>
          )}
        </div>

        <div className="qrposter-section">
          <h4>Template Content</h4>
          <div className="qrposter-fields">
            {fieldDefs.map((field) => (
              <label key={field.key} className="qrposter-field">
                {field.label}
                {field.multiline ? (
                  <textarea
                    rows={field.rows || 3}
                    value={activeFields?.[field.key] || ''}
                    onChange={(event) => updateTemplateField(field.key, event.target.value)}
                  />
                ) : (
                  <input
                    type="text"
                    value={activeFields?.[field.key] || ''}
                    onChange={(event) => updateTemplateField(field.key, event.target.value)}
                  />
                )}
                {field.help && <span className="qrposter-field-help">{field.help}</span>}
              </label>
            ))}
          </div>
          <div className="qrposter-field-hint">
            Placeholders: {'{PHONE} {EMAIL} {ADDRESS} {CITY} {RERA} {BRAND} {WEBSITE}'}
          </div>
        </div>

        <div className="qrposter-section">
          <h4>QR Image</h4>
          <div className="qrposter-upload">
            <button
              type="button"
              className="printable-action"
              onClick={handleFilePick}
              disabled={uploading}
            >
              {qrImageUrl ? 'Replace QR' : 'Upload QR Image'}
            </button>
            <input
              ref={fileInputRef}
              type="file"
              accept="image/png,image/jpeg"
              onChange={handleFileChange}
              style={{ display: 'none' }}
            />
            <div className="qrposter-upload-hint">{MAX_QR_SIZE_LABEL}</div>
          </div>
          <div className="qrposter-upload-meta">
            <div>
              Status:{' '}
              <strong>{qrImageUrl ? 'Uploaded' : 'Not uploaded'}</strong>
            </div>
            <div>File: {qrMeta.fileName || 'N/A'}</div>
            <div>
              Resolution:{' '}
              {qrMeta.width && qrMeta.height
                ? `${qrMeta.width}x${qrMeta.height}px`
                : 'N/A'}
            </div>
            <div>Size: {qrMeta.sizeLabel || 'N/A'}</div>
          </div>
        </div>

        <div className="qrposter-section">
          <h4>Poster Info</h4>
          <div className="printable-detail">
            <span>Phone</span>
            <strong>{profile.phone || 'Missing'}</strong>
          </div>
          <div className="printable-detail">
            <span>Email</span>
            <strong>{assignedEmail || 'Pending'}</strong>
          </div>
          <div className="printable-detail">
            <span>Address</span>
            <strong>{branch.address || 'Missing'}</strong>
          </div>
          {compliance.requireRera && (
            <div className="printable-detail">
              <span>RERA</span>
              <strong>{branch.reraId || 'Missing'}</strong>
            </div>
          )}
        </div>

        <div className="printable-actions">
          <label className="printable-checkbox">
            <input
              type="checkbox"
              checked={saveToAssets}
              onChange={(event) => setSaveToAssets(event.target.checked)}
            />
            Save to Assets Library
          </label>
          <button
            type="button"
            className="printable-action"
            disabled={exporting || isBlocked}
            onClick={handleExport}
          >
            {exporting ? 'Generating...' : 'Download PDF'}
          </button>
        </div>
      </div>
    </div>
  );
};

export default QRPosterPrintable;
